import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { v4 as uuidv4 } from 'uuid';

export const useAppSnackbar = defineStore('appSnackbar', () => {
  /**
   * Snackbar objects are vuetify snackbar props plus optional fields like:
   * onRemoved: Function to call when the snackbar is removed.
   * iconComponent: Vue component to use as the icon. Helpful for custom svgs.
   * iconColor: Color of the icon, defaults to the snackbar text color.
   * icon: mdi icon name. For example: $sync for mdiSync. Options in icons.js
   * textComponent: Vue component to use instead of the text. Useful for custom functionality
   * link: { text, to } shows a link after the text. "to" is a vue-router location.
   * Vuetify snackbar props: https://vuetifyjs.com/en/api/v-snackbar/
   */
  const snackbarQueue = ref([]);

  const activeSnackbar = computed(() => {
    if (snackbarQueue.value.length === 0) return null;
    return snackbarQueue.value[0];
  });

  const setSnackbarQueue = (newQueue) => {
    snackbarQueue.value = newQueue;
  };

  const addSnackbar = (snackbar) => {
    const newSnackbar = {
      ...snackbar,
      key: uuidv4(),
      modelValue: true, // Ensures the snackbar is shown immediately
      timeout: snackbar.timeout || 3000,
    };

    snackbarQueue.value.push(newSnackbar);
  };

  const addSuccessSnackbar = (props) => addSnackbar({
    color: 'success',
    icon: '$checkbox-marked-circle',
    ...props,
  });

  const addErrorSnackbar = (props) => addSnackbar({
    color: 'error',
    icon: '$warning',
    ...props,
  });

  const addInfoSnackbar = (props) => addSnackbar({
    color: 'green700',
    icon: '$information-outline',
    ...props,
  });

  const addMultilineErrorSnackbar = (props) => addErrorSnackbar({
    multiline: true,
    timeout: 5000,
    ...props,
  });

  // Removes the first snackbar in the queue. Useful for snackbars without a timeout
  const removeActiveSnackbar = () => {
    if (snackbarQueue.value.length === 0) return;

    const newQueue = [...snackbarQueue.value];
    const firstSnackbar = newQueue.shift();

    if (firstSnackbar.onRemoved) {
      firstSnackbar.onRemoved();
    }

    setSnackbarQueue(newQueue);
  };

  return {
    snackbarQueue,
    activeSnackbar,

    addSnackbar,
    addErrorSnackbar,
    addMultilineErrorSnackbar,
    addSuccessSnackbar,
    addInfoSnackbar,
    removeActiveSnackbar,
    setSnackbarQueue,
  };
});
